"use client";

// React & Nextjs imports
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useRouter } from "next/navigation";

// Models & Constants
import { useAuth } from "@/context/authcontext";

// Contexts
import { faqProductTypeMapping, FUNDAMENTAL_TABS, UsStock } from "@/app/interface/global";

// Hooks & Utils
// import useMobile from "@/app/hooks/use-mobile";
import { scrollToTop } from "@/app/utils/helper";

// Components
import AssetName from "@/app/global/components/asset-name-logo";
import AssetInformation from "@/app/global/components/asset-information";
// import AnalystRatings from "@/app/global/components/analyst-ratings";
// import ChartPriceForecast from "@/app/global/components/chart-price-forecast";
import FinancialMetrics from "@/app/global/components/fundamental-sheet";
import Faq from "@/app/components/faq";
import SuccessModal from "@/app/components/popup/success";
import ErrorModal from "@/app/components/popup/error";
// import { PriceAlert } from "@/app/global/components/price-alert";
import PerformancePosition from "@/app/global/components/performance-position";

// Skeleton
import AssetpageSkeleton from "@/app/components/loaders/assetpage-Skeleton";
import { PriceAlertNew } from "@/app/global/components/price-alert-new";
import { ProductRoute, productRoute } from "@/types/product";
import Breadcrumb, { BreadcrumbPath } from "@/app/components/breadcrumb";
import { InvestmentOptions } from "@/app/interface/platform";
import { DirectPaths } from "@/config/routes";
import { ProductFaqData } from "@/types/faq.model";
// import { FAQ } from "./global-constant";

type Props = {
    stockDetails: UsStock;
    faqData?: ProductFaqData | null | undefined;
};

export default function UsStockDetails({ stockDetails, faqData }: Props) {
    // const { isMobile } = useMobile();
    const router = useRouter();
    const params = useParams();
    const searchParams = useSearchParams();
    const { isAuthenticated } = useAuth();

    const countryProduct = params?.countryProduct as ProductRoute;
    const productId = params?.id ? params?.id[0] : null;
    const usStockName = params?.id ? params.id[1] : null;

    const [activeTab, setActiveTab] = useState<string>(
        countryProduct === productRoute["us-etfs"]
            ? FUNDAMENTAL_TABS.KEY_FINANCIALS
            : FUNDAMENTAL_TABS.FUNDAMENTALS,
    );
    const [errorStr /* , seterrorStr */] = useState<string>("");
    const [successStr /* , setsuccessStr */] = useState<string>("");
    const [isError, setisError] = useState<boolean>(false);
    const [isSuccess, setisSuccess] = useState<boolean>(false);

    // Handle canonical URL and redirection logic for product/company slugs
    useEffect(() => {
        if (isAuthenticated) {
            const meta_noindex = document.createElement("meta");
            meta_noindex.setAttribute("name", "robots");
            meta_noindex.setAttribute("content", "noindex");
            document.head.appendChild(meta_noindex);
        }

        if (!stockDetails) return;

        const companySlug = stockDetails?.company_name?.toLowerCase().replace(/\s+/gim, "-");

        if (typeof window != "undefined" && isAuthenticated) {
            window.sessionStorage.setItem("usStockName", companySlug);
            const msg = stockDetails?.company_name + " " + window.document.title;
            const meta_des = [...window.document.querySelectorAll("meta")].filter(
                (item) => item.name === "description",
            )?.[0];
            const meta_keyword = [...window.document.querySelectorAll("meta")].filter(
                (item) => item.name === "keywords",
            )?.[0];
            const link_canonical = [...window.document.querySelectorAll("link")].filter(
                (item) => item.rel === "canonical",
            )[0];

            window.document.title = msg;
            if (meta_des) {
                meta_des.setAttribute("content", msg);
            }
            if (meta_keyword) {
                meta_keyword.setAttribute(
                    "content",
                    stockDetails?.company_name + " - " + meta_keyword.content,
                );
            }
            if (link_canonical) {
                link_canonical.setAttribute("href", link_canonical.href + "/" + companySlug);
            }
        }
    }, [isAuthenticated, productId, usStockName, searchParams, router, stockDetails]);

    useEffect(() => {
        scrollToTop();
    }, []);

    if (!stockDetails) return null;

    const productLabel =
        countryProduct === productRoute["us-etfs"]
            ? InvestmentOptions.USEtf
            : InvestmentOptions.USStocks;

    return (
        <>
            {stockDetails ? (
                <>
                    <div className="w-full md:px-36">
                    <Breadcrumb
                        paths={
                            [
                                { label: "Home", route: "/" },
                                {
                                    label: productLabel,
                                    route: `${DirectPaths.GLOBAL}/${countryProduct}`,
                                },
                                { label: stockDetails.company_name ?? usStockName },
                            ] as BreadcrumbPath[]
                        }
                    />

                    <AssetName stockDetails={stockDetails} />

                    <div className="flex flex-col bg-white py-0 sm:container sm:py-6 md:flex-row md:justify-between md:gap-[30px]">
                        <div className="flex-col md:max-w-[calc(100%-340px)]">
                            {" "}
                            {/* max-w-[812px] */}
                            <AssetInformation stockDetails={stockDetails} />
                            <PerformancePosition stockDetails={stockDetails} />
                            <FinancialMetrics
                                stockDetails={stockDetails}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                            />
                            {/* <AnalystRatings stockDetails={stockDetails} /> */}
                            {/* {!isNaN(stockDetails?.forecast?.target_high) &&
                            !isNaN(stockDetails?.forecast?.target_low) ? (
                                <ChartPriceForecast stockDetails={stockDetails} />
                            ) : null} */}
                            {activeTab !== "FAQs" && (
                                <div className="-mt-8 xs:mt-0">
                                    <Faq
                                        bgColor="bg-white"
                                        compact={true}
                                        product={
                                            countryProduct === productRoute["us-stocks"]
                                                ? faqProductTypeMapping.GLOBAL // faqProductTypeMapping.US_STOCKS
                                                : (countryProduct === productRoute["us-etfs"] &&
                                                      faqProductTypeMapping.US_ETF) ||
                                                  "" // enable "global" in case of show custom dynamic FAQs for ETF.
                                        }
                                        //subProduct=""
                                        initialFaqData={faqData}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="sm-mb-0 mb-20 w-full xs:mb-8 sm:mt-[1] sm:max-w-[300px]">
                            <PriceAlertNew />
                        </div>
                    </div>
                    </div>

                    {isError && <ErrorModal errorStr={errorStr ?? ""} onClose={setisError} />}
                    {isSuccess && (
                        <SuccessModal successStr={successStr ?? ""} onClose={setisSuccess} />
                    )}
                </>
            ) : (
                <div className="flex h-screen items-center justify-center">
                    <AssetpageSkeleton />
                </div>
            )}
        </>
    );
}
